import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Filter, RotateCcw } from 'lucide-react';
import type { PerformanceFilterOption } from '../../../lib/api';
import type { PerformanceFilterState, PeriodPreset } from './useSupplierPerformance';

interface PerformanceFiltersProps {
  filters: PerformanceFilterState;
  onChange: (next: PerformanceFilterState) => void;
  onReset: () => void;
  divisions: PerformanceFilterOption[];
  suppliers: PerformanceFilterOption[];
  /** Resolved range of the data on screen, YYYY-MM. */
  range: { from: string; to: string } | null;
  /** Hide the supplier picker (a supplier only ever sees itself). */
  hideSupplier?: boolean;
}

const PRESETS: { value: PeriodPreset; label: string }[] = [
  { value: '12', label: 'Rolling 12 months' },
  { value: '24', label: 'Last 24 months' },
  { value: '36', label: 'Last 36 months' },
  { value: 'all', label: 'All history' },
  { value: 'custom', label: 'Custom range' },
];

export const UNASSIGNED_DIVISION = 'unassigned';

/**
 * Period, division and supplier filters for the supplier performance dashboard.
 * Rolling twelve months is the default; the range can be widened or set by month.
 */
export default function PerformanceFilters({
  filters, onChange, onReset, divisions, suppliers, range, hideSupplier = false,
}: PerformanceFiltersProps) {
  const divisionOptions = [
    { value: '', label: 'All divisions' },
    ...divisions.map((d) => ({ value: String(d.id), label: d.name })),
    { value: UNASSIGNED_DIVISION, label: 'Unassigned (no division on the PO)' },
  ];
  const supplierOptions = [
    { value: '', label: 'All suppliers' },
    ...suppliers.map((s) => ({ value: String(s.id), label: s.name })),
  ];
  const isDefault = filters.preset === '12' && !filters.division && !filters.supplier;

  const setPreset = (preset: PeriodPreset) => {
    if (preset === 'custom') {
      onChange({ ...filters, preset, from: filters.from || range?.from || '', to: filters.to || range?.to || '' });
    } else {
      onChange({ ...filters, preset });
    }
  };

  return (
    <Card data-testid="supplier-performance-filters">
      <CardContent className="flex flex-wrap items-end gap-3 py-4">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground self-center">
          <Filter className="h-4 w-4" />
          Filters
        </div>
        <div className="w-48">
          <label className="mb-1 block text-xs text-muted-foreground">Period</label>
          <Select value={filters.preset} onValueChange={(v) => setPreset(v as PeriodPreset)}>
            <SelectTrigger data-testid="supplier-performance-period" aria-label="Period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {filters.preset === 'custom' && (
          <>
            <div className="w-40">
              <label className="mb-1 block text-xs text-muted-foreground" htmlFor="perf-from">From</label>
              <Input id="perf-from" type="month" value={filters.from}
                onChange={(e) => onChange({ ...filters, from: e.target.value })} data-testid="supplier-performance-from" />
            </div>
            <div className="w-40">
              <label className="mb-1 block text-xs text-muted-foreground" htmlFor="perf-to">To</label>
              <Input id="perf-to" type="month" value={filters.to}
                onChange={(e) => onChange({ ...filters, to: e.target.value })} data-testid="supplier-performance-to" />
            </div>
          </>
        )}
        <div className="w-64" data-testid="supplier-performance-division">
          <label className="mb-1 block text-xs text-muted-foreground">Division</label>
          <SearchableSelect options={divisionOptions} value={filters.division} placeholder="All divisions"
            onChange={(v) => onChange({ ...filters, division: v })} />
        </div>
        {!hideSupplier && (
          <div className="w-72" data-testid="supplier-performance-supplier">
            <label className="mb-1 block text-xs text-muted-foreground">Supplier</label>
            <SearchableSelect options={supplierOptions} value={filters.supplier} placeholder="All suppliers"
              onChange={(v) => onChange({ ...filters, supplier: v })} />
          </div>
        )}
        <Button variant="outline" size="sm" onClick={onReset} disabled={isDefault} data-testid="supplier-performance-reset">
          <RotateCcw className="mr-2 h-4 w-4" />
          Reset
        </Button>
      </CardContent>
    </Card>
  );
}
